// Reference-image control. Single or multiple images; each thumbnail has its own
// remove button. Uploads via /api/assets and reports the stored filenames.
import { api } from "../api";

interface SingleOptions {
  url: string | null;
  onChange: (file: string | null, url: string | null) => void;
}

/** Backwards-compatible single-image field. */
export function createImageField(opts: SingleOptions): HTMLElement {
  return createImageListField({
    urls: opts.url ? [opts.url] : [],
    max: 1,
    onChange: (_files, urls) => opts.onChange(null, urls[0] ?? null),
  });
}

interface ListOptions {
  /** current image URLs (`/uploads/<file>`) */
  urls: string[];
  max?: number;
  /** called with the full filename + url lists after every add/remove */
  onChange: (files: string[], urls: string[]) => void;
  /** view-only (public share link) — show photos, no add/remove controls */
  readOnly?: boolean;
}

const fileOf = (url: string): string => url.split("/").pop() || url;

export function createImageListField(opts: ListOptions): HTMLElement {
  const max = opts.max ?? 12;
  let urls = [...opts.urls];

  const wrap = document.createElement("div");
  wrap.className = "imgfield";

  const strip = document.createElement("div");
  strip.className = "imgfield-strip";

  if (opts.readOnly) {
    urls.forEach((url) => {
      const t = document.createElement("div");
      t.className = "imgfield-thumb has-img";
      t.style.backgroundImage = `url("${url}")`;
      strip.appendChild(t);
    });
    wrap.append(strip);
    return wrap;
  }

  const pick = document.createElement("button");
  pick.type = "button";
  pick.className = "btn sm";

  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/png,image/jpeg,image/webp";
  input.multiple = true;
  input.hidden = true;

  const status = document.createElement("span");
  status.className = "imgfield-status";

  const emit = () => opts.onChange(urls.map(fileOf), [...urls]);

  const paint = () => {
    strip.replaceChildren();
    urls.forEach((url, i) => {
      const t = document.createElement("div");
      t.className = "imgfield-thumb has-img";
      t.style.backgroundImage = `url("${url}")`;
      const x = document.createElement("button");
      x.type = "button";
      x.className = "imgfield-thumb-x";
      x.textContent = "×";
      x.title = "Bild entfernen";
      x.addEventListener("click", () => {
        urls.splice(i, 1);
        paint();
        emit();
      });
      t.appendChild(x);
      strip.appendChild(t);
    });
    pick.textContent = max === 1 ? (urls.length ? "Bild ersetzen" : "Bild wählen") : "+ Bild";
    pick.hidden = urls.length >= max;
  };
  paint();

  pick.addEventListener("click", () => input.click());
  input.addEventListener("change", async () => {
    const files = [...(input.files ?? [])];
    input.value = "";
    if (!files.length) return;
    status.textContent = "lädt…";
    try {
      for (const f of files) {
        if (urls.length >= max) break;
        const res = await api.uploadImage(f);
        if (max === 1) urls = [res.url];
        else urls.push(res.url);
      }
      status.textContent = "";
      paint();
      emit();
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : "Upload fehlgeschlagen";
    }
  });

  const controls = document.createElement("div");
  controls.className = "imgfield-controls";
  controls.append(pick, status);

  wrap.append(strip, controls, input);
  return wrap;
}
